import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BlogPost } from "../../types/blog";
import blogService from "../../services/blogService";

interface BlogState {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
}

const initialState: BlogState = {
  posts: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchPosts = createAsyncThunk(
  'blog/fetchPosts',
  async (_, { rejectWithValue }) => {
    try {
      return await blogService.getAllPosts();
    } catch (error: any) {
      return rejectWithValue(error.message || "Impossible de charger les articles");
    }
  }
);

export const createBlogPost = createAsyncThunk<
  string,
  {
    title: string;
    content: string;
    type: 'film' | 'serie';
    tmdbId: number;
    year: string;
    image: string;
    poster: string;
    overview: string;
    rating: number;
    tags: string[];
    authorId: string;
  }
>(
  'blog/createPost',
  async (post, { rejectWithValue }) => {
    try {
      const id = await blogService.createPost(post);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Impossible de créer l'article");
    }
  }
);

export const updateBlogPost = createAsyncThunk<
  BlogPost,
  { id: string; post: Partial<Omit<BlogPost, 'id'>> }
>(
  'blog/updatePost',
  async ({ id, post }, { rejectWithValue }) => {
    try {
      await blogService.updatePost(id, post);
      return { id, ...post } as BlogPost;
    } catch (error: any) {
      return rejectWithValue(error.message || "Impossible de mettre à jour l'article");
    }
  }
);

export const deleteBlogPost = createAsyncThunk<
  string,
  string
>(
  'blog/deletePost',
  async (id, { rejectWithValue }) => {
    try {
      await blogService.deletePost(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Impossible de supprimer l'article");
    }
  }
);

const blogSlice = createSlice({
  name: "blog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch posts
      .addCase(fetchPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = action.payload;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Une erreur est survenue";
      })
      // Create post
      .addCase(createBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBlogPost.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Impossible de créer l'article";
      })
      // Update post
      .addCase(updateBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.posts.findIndex(post => post.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
      })
      .addCase(updateBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Impossible de mettre à jour l'article";
      })
      // Delete post
      .addCase(deleteBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.filter(post => post.id !== action.payload);
      })
      .addCase(deleteBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Impossible de supprimer l'article";
      });
  },
});

export default blogSlice.reducer;
